import { supabase } from "@/integrations/supabase/client";
import {
  EAPI_TRAILER_TYPES,
  isEapiTrailerType,
  type EapiTrailerType,
} from "../../supabase/functions/_shared/models/trailer/trailer-type.ts";

export type EapiEnvironment = "dev" | "staging" | "pre-prod" | "prod";

export type AymsEventType = "trailer_check_in" | "trailer_check_out";
const AYMS_EVENT_TYPES: ReadonlySet<AymsEventType> = new Set(["trailer_check_in", "trailer_check_out"]);
/** Trailer type vocabulary shared with the inbound trailer-arrival check-in. */
export type AymsTrailerType = EapiTrailerType;
export interface AymsEventLocation {
  /** ATMS `locations.id`, the same id used in the trailer-arrival check-in path. */
  id: string;
  /** One fixed WGS84 point for the location, identical for every event there. */
  lat: number;
  long: number;
}
export interface AymsEventPayload {
  event_id: string;
  event_type: AymsEventType;
  trailer_id: string;
  trailer_type?: AymsTrailerType;
  /** Tractor that brought the trailer in / picked it up, as AYMS recorded it. */
  tractor_id?: string;
  location: AymsEventLocation;
  /** Present only when the trailer is on a load. */
  customer_load_id?: string;
  org_id: string;
  timestamp: string;
}
const RFC3339_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?(?:Z|[+-]\d{2}:?\d{2})$/;

function assertNonEmptyString(value: unknown, field: string): asserts value is string {
  if (typeof value !== "string" || value.trim() === "") {
    throw new Error(`Missing or invalid required field: ${field}`);
  }
}

function assertPositiveNumber(value: unknown, field: string): asserts value is number {
  if (typeof value !== "number" || !Number.isFinite(value) || value <= 0) {
    throw new Error(`Missing or invalid required field: ${field} (must be a positive number)`);
  }
}

async function assertOrderExistsByUniqueId(uniqueId: string): Promise<void> {
  const { data, error } = await supabase
    .from("orders")
    .select("id")
    .eq("unique_id", uniqueId)
    .limit(1)
    .maybeSingle();
  if (error) {
    throw new Error(`Failed to validate vas_trip_id: ${error.message}`);
  }
  if (!data) {
    throw new Error(`Unknown vas_trip_id: no order found with unique_id=${uniqueId}`);
  }
}

export class EapiClient {
  private environment: EapiEnvironment;

  constructor(environment: EapiEnvironment) {
    if (!environment) {
      throw new Error("EapiClient requires an explicit environment");
    }
    this.environment = environment;
  }

  setEnvironment(environment: EapiEnvironment) {
    this.environment = environment;
  }

  private async invoke(method: string, path: string, body?: unknown) {
    const { data, error } = await supabase.functions.invoke("eapi-proxy", {
      body: { method, path, body, environment: this.environment },
    });

    if (error) {
      throw new Error(`EAPI request failed: ${error.message}`);
    }

    // The proxy wraps the upstream response as { status, data }.
    // A non-2xx upstream status means the EAPI call failed.
    if (data && typeof data.status === "number" && (data.status < 200 || data.status >= 300)) {
      const detail = typeof data.data === "object" ? JSON.stringify(data.data) : String(data.data ?? "");
      if (method === "POST" && path === "/v1/trips" && data.status === 400 && detail.includes("failed to parse request")) {
        throw new Error(
          "EAPI could not create this trip. The selected tractor/VIN may not be registered in the EAPI environment for this tenant.",
        );
      }
      throw new Error(`EAPI ${method} ${path} returned ${data.status}: ${detail}`);
    }

    return data;
  }

  /** GET /v1/trips/:id */
  async getTrip(id: string) {
    return this.invoke("GET", `/v1/trips/${id}`);
  }

  /** POST /v1/trips */
  async createTrip(data?: unknown) {
    return this.invoke("POST", "/v1/trips", data ?? {});
  }

  /** PATCH /v1/trips/:id — partial update (origin, destination, windows, trailer) */
  async patchTrip(id: string, data: unknown) {
    return this.invoke("PATCH", `/v1/trips/${id}`, data);
  }

  /** POST /v1/trips/:id/cancel — cancel a trip with optional reason */
  async cancelTrip(id: string, reason?: string) {
    const body = reason ? { reason } : {};
    return this.invoke("POST", `/v1/trips/${id}/cancel`, body);
  }

  /** POST /v1/trips/:id/abandon — abandon a trip with optional reason */
  async abandonTrip(id: string, reason?: string) {
    const body = reason ? { reason } : {};
    return this.invoke("POST", `/v1/trips/${id}/abandon`, body);
  }

  /** POST /v1/trips/status — batch status lookup */
  async getTripStatuses(tripIds: string[]) {
    return this.invoke("POST", "/v1/trips/status", { trip_ids: tripIds });
  }

  /** GET /v1/custom-field-definitions — tenant-scoped custom field metadata */
  async getCustomFieldDefinitions() {
    return this.invoke("GET", "/v1/custom-field-definitions");
  }

  /**
   * POST /v1/vas/weight — submit combined weight for a VAS trip.
   * `vas_trip_id` is the order's `unique_id`. `org_id` is forwarded verbatim
   * to EAPI and is intentionally decoupled from session/Onramp org claims.
   */
  async submitVasWeight(params: {
    vas_trip_id: string;
    combined_weight_lbs: number;
    org_id: string;
  }): Promise<void> {
    assertNonEmptyString(params?.vas_trip_id, "vas_trip_id");
    assertPositiveNumber(params?.combined_weight_lbs, "combined_weight_lbs");
    assertNonEmptyString(params?.org_id, "org_id");
    await assertOrderExistsByUniqueId(params.vas_trip_id);
    await this.invoke("POST", "/v1/vas/weight", {
      vas_trip_id: params.vas_trip_id,
      combined_weight_lbs: params.combined_weight_lbs,
      org_id: params.org_id,
    });
  }

  /**
   * POST /v1/vas/stage-transition — report a stage transition for a VAS trip.
   * `vas_trip_id` is the order's `unique_id`. `org_id` is forwarded verbatim
   * to EAPI and is intentionally decoupled from session/Onramp org claims.
   */
  async submitVasStageTransition(params: {
    vas_trip_id: string;
    current_stage: string;
    org_id: string;
  }): Promise<void> {
    assertNonEmptyString(params?.vas_trip_id, "vas_trip_id");
    assertNonEmptyString(params?.current_stage, "current_stage");
    assertNonEmptyString(params?.org_id, "org_id");
    await assertOrderExistsByUniqueId(params.vas_trip_id);
    await this.invoke("POST", "/v1/vas/stage-transition", {
      vas_trip_id: params.vas_trip_id,
      current_stage: params.current_stage,
      org_id: params.org_id,
    });
  }

  /**
   * POST /v1/ayms/events — report an AYMS trailer yard event (ASE-20496).
   * Caller is expected to have already resolved the associated order, so this
   * intentionally skips the unique_id existence check that `vas/*` helpers do.
   * `customer_load_id` is optional: trailers that are not on a load still
   * produce an event, just without one. Optional fields are omitted from the
   * body rather than sent as null.
   */
  async submitAymsEvent(params: AymsEventPayload): Promise<void> {
    assertNonEmptyString(params?.event_id, "event_id");
    assertNonEmptyString(params?.event_type, "event_type");
    if (!AYMS_EVENT_TYPES.has(params.event_type as AymsEventType)) {
      throw new Error(
        `Invalid event_type: ${params.event_type}. Must be one of: trailer_check_in, trailer_check_out`,
      );
    }
    assertNonEmptyString(params?.trailer_id, "trailer_id");
    if (params.customer_load_id !== undefined) {
      assertNonEmptyString(params.customer_load_id, "customer_load_id");
    }
    if (params.trailer_type !== undefined && !isEapiTrailerType(params.trailer_type)) {
      throw new Error(
        `Invalid trailer_type: ${params.trailer_type}. Must be one of: ${EAPI_TRAILER_TYPES.join(", ")}`,
      );
    }
    if (params.tractor_id !== undefined) {
      assertNonEmptyString(params.tractor_id, "tractor_id");
    }
    assertNonEmptyString(params?.location?.id, "location.id");
    if (!Number.isFinite(params.location.lat) || !Number.isFinite(params.location.long)) {
      throw new Error("location.lat and location.long must be finite numbers");
    }
    assertNonEmptyString(params?.org_id, "org_id");
    assertNonEmptyString(params?.timestamp, "timestamp");
    if (!RFC3339_RE.test(params.timestamp)) {
      throw new Error(`Invalid timestamp: ${params.timestamp} (must be RFC3339)`);
    }
    await this.invoke("POST", "/v1/ayms/events", {
      event_id: params.event_id,
      event_type: params.event_type,
      trailer_id: params.trailer_id,
      ...(params.trailer_type !== undefined ? { trailer_type: params.trailer_type } : {}),
      ...(params.tractor_id !== undefined ? { tractor_id: params.tractor_id } : {}),
      location: { id: params.location.id, lat: params.location.lat, long: params.location.long },
      ...(params.customer_load_id !== undefined ? { customer_load_id: params.customer_load_id } : {}),
      org_id: params.org_id,
      timestamp: params.timestamp,
    });
  }
}
